import React, { useEffect, useMemo, useState } from 'react';
import { Button, Grid, IconButton, InputAdornment, Stack, Typography } from '@mui/material';
import CustomTextInput from '../../../components/CustomTextInput';
import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { authApiClient } from '../../../utils/axios';
import { showSnackbar } from '../../../api/snackbarSlice';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { useRevalidateFormOnLanguageChange } from '../../../hooks/useRevalidateFormOnLanguageChange';
import appConfig from '../../../config/appConfig';

export const createUpdatePasswordSchema = (t) =>
  Yup.object().shape({
    email: Yup.string().email(t.invalid_email_format).required(t.email_required),
    new_password: Yup.string()
      .min(8, t.password_min_8)
      .matches(/[A-Z]/, t.password_uppercase)
      .matches(/[a-z]/, t.password_lowercase)
      .matches(/\d/, t.password_number)
      .matches(/[@$!%*?&]/, t.password_special)
      .required(t.password_required),
    confirmPassword: Yup.string()
      .required(t.confirm_password_required)
      .oneOf([Yup.ref('new_password')], t.passwords_must_match),
    otp: Yup.string()
      .required(t.otp_required)
      .matches(/^\d{6}$/, t.otp_must_be_6_digits)
      .length(6, t.otp_must_be_6_digits),
  });

const ForgotPasswordUpdateForm = ({ email }) => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [timer, setTimer] = useState(120);
  const dispatch = useDispatch();
  const { snackbarMessages, forgotPassword: fp, language } = useTranslatedMessages();
  const authPasswordMessages = snackbarMessages.auth_password ?? {};
  const authOtpMessages = snackbarMessages.auth_otp ?? {};
  const navigate = useNavigate();
  const v = fp.validations ?? {};
  const schema = useMemo(
    () =>
      createUpdatePasswordSchema({
        invalid_email_format: v.invalid_email_format ?? 'Invalid email format',
        email_required: v.email_required ?? 'Email is required',
        password_min_8: v.password_min_8 ?? 'Password must be at least 8 characters',
        password_uppercase: v.password_uppercase ?? 'Password must contain at least one uppercase letter',
        password_lowercase: v.password_lowercase ?? 'Password must contain at least one lowercase letter',
        password_number: v.password_number ?? 'Password must contain at least one number',
        password_special:
          v.password_special ?? 'Password must contain at least one special character (@, $, !, %, *, ?, &)',
        password_required: v.password_required ?? 'Password is required',
        confirm_password_required: v.confirm_password_required ?? 'Confirm password is required',
        passwords_must_match: v.passwords_must_match ?? 'Passwords must match',
        otp_required: v.otp_required ?? 'OTP is required',
        otp_must_be_6_digits: v.otp_must_be_6_digits ?? 'OTP must be exactly 6 digits',
      }),
    [v],
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
    setError,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: email,
      termsAndCondition: false,
    },
  });

  useRevalidateFormOnLanguageChange(language, trigger, errors);

  useEffect(() => {
    if (timer > 0) {
      const countdown = setInterval(() => {
        setTimer(timer - 1);
      }, 1000);
      return () => clearInterval(countdown);
    }
  }, [timer]);

  const onSubmit = async (data) => {
    setLoading(true);
    authApiClient
      .post('/reset-password', data)
      .then((res) => {
        navigate('/login', { replace: true });
        dispatch(
          showSnackbar({
            message: authPasswordMessages.password_updated_success,
            severity: 'success',
          }),
        );
      })
      .catch((err) => {
        if (err?.response?.data?.errors?.new_password) {
          setError('new_password', { message: err?.response?.data?.errors?.new_password });
        } else {
          let message =
            Object.values(err?.response?.data?.errors ?? [])
              .flat()
              .join('\n') ||
            err.message ||
            authPasswordMessages.password_generic_error;
          dispatch(showSnackbar({ message, severity: 'error' }));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleResendOtp = () => {
    authApiClient
      .post('/forgot-password', { email: email })
      .then((res) => {
        dispatch(showSnackbar({ message: authOtpMessages.otp_sent_success, severity: 'success' }));
        setTimer(120);
      })
      .catch((err) => {
        dispatch(
          showSnackbar({
            message:
              err?.response?.data?.errors?.message ??
              fp.snackbar?.error_sending_otp ??
              'Error sending OTP. Please try again.',
            severity: 'error',
          }),
        );
      });
  };

  const formData = watch();
  return (
    <>
      <Stack sx={{ alignItems: 'center', mb: 4 }}>
        <img src={appConfig.brandLogoPath} alt="logo" width="50%" />
      </Stack>

      <Stack spacing={2}>
        <CustomTextInput
          name="email"
          label={fp.update_form?.email_label ?? 'Email'}
          control={control}
          errors={errors}
          disabled={true}
        />
        <CustomTextInput
          name="otp"
          label={fp.update_form?.otp_label ?? 'OTP'}
          control={control}
          errors={errors}
          placeholder={fp.update_form?.otp_placeholder ?? 'Enter 6-digit OTP'}
          inputProps={{ maxLength: 6 }}
        />
        <Grid container spacing={2} sx={{ justifyContent: 'right' }}>
          {timer > 0 ? (
            <Typography variant="body2" color="textSecondary" align="right">
              {(fp.update_form?.resend_otp_in ?? 'Resend OTP in {minutes}:{seconds}')
                .replace('{minutes}', String(Math.floor(timer / 60)))
                .replace('{seconds}', String(timer % 60).padStart(2, '0'))}
            </Typography>
          ) : (
            <Button onClick={handleResendOtp} variant="text" color="primary">
              {fp.update_form?.resend_otp ?? 'Resend OTP'}
            </Button>
          )}
        </Grid>
        <CustomTextInput
          name="new_password"
          type={showPassword ? 'text' : 'password'}
          label={fp.update_form?.password_label ?? 'Password'}
          control={control}
          errors={errors}
          endAdornment={
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
        />
        <CustomTextInput
          name="confirmPassword"
          type={showConfirmPassword ? 'text' : 'password'}
          label={fp.update_form?.confirm_password_label ?? 'Confirm Password'}
          control={control}
          errors={errors}
          endAdornment={
            <InputAdornment position="end">
              <IconButton onClick={() => setShowConfirmPassword((prev) => !prev)} edge="end">
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
        />
        <Grid container spacing={0}>
          <Grid size={6}>
            <Typography variant="caption" sx={{ color: formData.new_password?.length >= 8 ? 'green' : '#ff0000' }}>
              {fp.update_form?.password_rules?.min_8 ?? 'Minimum 8 characters.'}
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography variant="caption" sx={{ color: /[A-Z]/.test(formData.new_password) ? 'green' : '#ff0000' }}>
              {fp.update_form?.password_rules?.uppercase ?? 'At least 1 uppercase letter.'}
            </Typography>
          </Grid>

          <Grid size={6}>
            <Typography variant="caption" sx={{ color: /[0-9]/.test(formData.new_password) ? 'green' : '#ff0000' }}>
              {fp.update_form?.password_rules?.number ?? 'At least 1 number.'}
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography
              variant="caption"
              sx={{ color: /[!@#$%^&*(),.?":{}|<>]/.test(formData.new_password) ? 'green' : '#ff0000' }}
            >
              {fp.update_form?.password_rules?.special ?? 'At least one special character.'}
            </Typography>
          </Grid>
        </Grid>
        <Button type="button" variant="contained" className="submit" onClick={handleSubmit(onSubmit)}>
          {loading
            ? (fp.update_form?.updating ?? 'Updating...')
            : (fp.update_form?.update_password ?? 'Update Password')}
        </Button>
        <Typography variant="body2">
          <Link to="/login" style={{ textDecoration: 'none' }}>
            {fp.update_form?.back_to_login ?? 'Back to Login'}
          </Link>
        </Typography>
      </Stack>
    </>
  );
};

export default ForgotPasswordUpdateForm;
